import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Text } from './Texte';
import { ScoreRing } from './ScoreRing';
import { ContenuLtr } from './ContenuLtr';
import { Heart, X } from './icons';
import { OffreDetail, type OffreDetailProps } from './OffreDetail';
import { PastilleEntreprise, lieuAffiche } from './OffreCarte';
import { t } from '../i18n';
import { TEXTE_LTR } from '../i18n/sens';
import { colors, gradients, layout, radius, shadow, spacing, typography } from '../theme/tokens';
import type { Decision, Offre } from '../types/offres';
import { sourceLibelle } from '../utils/offresAffichage';

export interface OffresDeuxVoletsProps {
  /** Liste affichée à gauche. */
  offres: Offre[];
  /** Offre ouverte à droite ; null si la liste est vide. */
  affichee: Offre | null;
  onChoisir: (offre: Offre) => void;
  /** Absent pour « Sauvegardées » : on y consulte, on ne décide plus. */
  onDecision?: (offre: Offre, decision: Decision) => void;
  detail: Pick<OffreDetailProps, 'sauvegardee' | 'onSauvegarder' | 'age' | 'naissanceConnue' | 'onNaissance' | 'onDossierPret'>;
}

/** Ligne de la liste : assez d'informations pour comparer sans ouvrir, comme une boîte de réception. */
function LigneOffre({ offre, choisie, onPress }: { offre: Offre; choisie: boolean; onPress: () => void }) {
  const [survol, setSurvol] = useState(false);
  const contrat = t(`offres.contrats.${offre.contrat.code}`);
  const lieu = lieuAffiche(offre);
  const sous = [offre.entreprise.nom, sourceLibelle(offre.source, offre.partenaire)].filter(Boolean).join(' · ');

  return (
    <Pressable
      testID={`offre-ligne-${offre.cle}`}
      accessibilityRole="button"
      accessibilityState={{ selected: choisie }}
      accessibilityLabel={[offre.titre, offre.entreprise.nom, t('offres.carte.score', { score: offre.score })]
        .filter(Boolean)
        .join(', ')}
      onPress={onPress}
      onHoverIn={() => setSurvol(true)}
      onHoverOut={() => setSurvol(false)}
      style={[styles.ligne, survol && styles.ligneSurvol, choisie && styles.ligneChoisie]}
    >
      <PastilleEntreprise offre={offre} taille={spacing.xxl + spacing.md} />
      <View style={styles.ligneTextes}>
        <ContenuLtr>
          <Text style={[styles.ligneTitre, TEXTE_LTR]} numberOfLines={2}>
            {offre.titre}
          </Text>
        </ContenuLtr>
        <ContenuLtr>
          <Text style={[styles.ligneSous, TEXTE_LTR]} numberOfLines={1}>
            {sous}
          </Text>
        </ContenuLtr>
        <View style={styles.ligneTags}>
          <View style={styles.tagContrat}>
            <Text style={styles.tagContratTexte}>{contrat}</Text>
          </View>
          {lieu ? (
            <ContenuLtr style={styles.tagLieu}>
              <Text style={[styles.ligneSous, TEXTE_LTR]} numberOfLines={1}>
                {lieu}
              </Text>
            </ContenuLtr>
          ) : null}
        </View>
      </View>
      <ScoreRing score={offre.score} size={spacing.xxl + spacing.lg} variante="marque" />
    </Pressable>
  );
}

/**
 * Offres sur ordinateur : une liste à gauche, la fiche complète à droite.
 *
 * La pile à swiper est un geste de pouce ; avec une souris elle montre UNE offre à la fois et
 * cache les autres. Ici on compare, on parcourt, et la fiche (critères, description, source,
 * préparation du dossier) reste ouverte à côté sans fenêtre par-dessus.
 */
export function OffresDeuxVolets({ offres, affichee, onChoisir, onDecision, detail }: OffresDeuxVoletsProps) {
  return (
    <View style={styles.racine} testID="offres-deux-volets">
      <ScrollView
        testID="offres-liste"
        style={styles.volet}
        contentContainerStyle={styles.liste}
        showsVerticalScrollIndicator={false}
      >
        {offres.map((o) => (
          <LigneOffre key={o.cle} offre={o} choisie={affichee?.cle === o.cle} onPress={() => onChoisir(o)} />
        ))}
      </ScrollView>

      <View style={styles.fiche}>
        {affichee && onDecision ? (
          <View style={styles.actions}>
            <Pressable
              testID="offre-action-passer"
              accessibilityRole="button"
              accessibilityLabel={t('offres.actions.passer')}
              onPress={() => onDecision(affichee, 'passee')}
              style={styles.passer}
            >
              <X size={20} color={colors.grey500} strokeWidth={2.6} />
              <Text style={styles.passerTexte}>{t('offres.actions.libelles.passer')}</Text>
            </Pressable>
            <Pressable
              testID="offre-action-interesse"
              accessibilityRole="button"
              accessibilityLabel={t('offres.actions.interesse')}
              onPress={() => onDecision(affichee, 'interessee')}
              style={styles.interesse}
            >
              <LinearGradient
                colors={gradients.cta}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.interesseFond}
              >
                <Heart size={18} color={colors.white} fill={colors.white} />
                <Text style={styles.interesseTexte}>{t('offres.actions.libelles.interesse')}</Text>
              </LinearGradient>
            </Pressable>
          </View>
        ) : null}
        <OffreDetail enligne offre={affichee} onFermer={() => undefined} {...detail} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  racine: { flex: 1, flexDirection: 'row', gap: spacing.xl, minHeight: 0 },
  volet: { width: layout.voletPrincipalLargeur, flexGrow: 0, flexShrink: 0 },
  liste: { gap: spacing.sm, paddingBottom: spacing.xxl },

  ligne: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: 'transparent',
    cursor: 'pointer',
    ...shadow.card,
  },
  ligneSurvol: { borderColor: colors.bordureClaire },
  ligneChoisie: { borderColor: colors.electricDeep, backgroundColor: colors.blueSoft },
  ligneTextes: { flex: 1, minWidth: 0, gap: 2 },
  ligneTitre: {
    fontSize: typography.bodyMd.size,
    lineHeight: typography.bodyMd.lineHeight,
    fontWeight: '700',
    color: colors.navy,
  },
  ligneSous: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  ligneTags: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xs },
  tagContrat: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radius.pill,
    backgroundColor: colors.blueSoft,
  },
  tagContratTexte: { fontSize: typography.small.size, fontWeight: '700', color: colors.electricInk },
  tagLieu: { flexShrink: 1, minWidth: 0 },

  fiche: { flex: 1, minWidth: 0, minHeight: 0, gap: spacing.md },
  actions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: spacing.md },
  passer: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.xl,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.bordureClaire,
    cursor: 'pointer',
  },
  passerTexte: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.grey500 },
  interesse: { borderRadius: radius.pill, cursor: 'pointer', ...shadow.card },
  interesseFond: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.xl,
    borderRadius: radius.pill,
  },
  interesseTexte: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.white },
});
